import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { secureAxiosInstanceWithErrorMessageInterceptor } from '../api-service/secure-axios-instance';
import { useNotifier } from '../components/notifier/notifier-context';
import { FasitProblemDetails } from '../api-service/clients';

function parseFasitFromAny(data: unknown): FasitProblemDetails | null {
  if (!data) return null;

  // Already an object of the right shape
  if (typeof data === 'object' && !(data instanceof Blob)) {
    return data as FasitProblemDetails;
  }

  // JSON as string
  if (typeof data === 'string') {
    try {
      return JSON.parse(data) as FasitProblemDetails;
    } catch {
      return null;
    }
  }

  // Blob case will be handled outside (async)
  return null;
}

export function ErrorMessageInterceptor() {
  const { dispatch } = useNotifier();

  React.useEffect(() => {
    secureAxiosInstanceWithErrorMessageInterceptor.interceptors.response.use(
      (response: AxiosResponse) => response,

      async (error: AxiosError<any>) => {
        let isDispatched = false;

        const { response } = error;

        let problem: FasitProblemDetails | null = null;

        // Case 1: data is already usable or string
        if (response && !(response.data instanceof Blob)) {
          problem = parseFasitFromAny(response.data);
        }

        // Case 2: data is Blob (e.g. from file/stream endpoint)
        if (!problem && response && response.data instanceof Blob) {
          try {
            const text = await response.data.text();
            problem = parseFasitFromAny(text);
          } catch {
            // ignore, fall through
          }
        }

        // At this point, problem is either a parsed FasitProblemDetails or null
        if (problem) {
          const { developerDetail, helpLink, messageTitle, correlationIdentifier, status, detail, title, timestamp, severity } = problem;

          try {
            // Special Dfdg handling
            if (developerDetail?.includes('"systemName":"Dfdg"')) {
              const responseString = developerDetail.split('Response: ')[1];
              const responseObject = JSON.parse(responseString);
              const {
                helpLink: innerHelpLink,
                messageTitle: innerMessageTitle,
                correlationIdentifier: innerCorrelationIdentifier,
                status: innerStatus,
                detail: innerDetail,
                title: innerTitle,
                timestamp: innerTimestamp,
              } = responseObject;

              dispatch({
                type: 'notify',
                payload: {
                  correlationIdentifier: innerCorrelationIdentifier,
                  severity: 'error',
                  status: innerStatus,
                  detail: innerDetail,
                  title: innerTitle,
                  timestamp: innerTimestamp,
                  helpLink: innerHelpLink,
                  messageTitle: innerMessageTitle,
                },
              });

              isDispatched = true;
            }
          } catch {
            // Resume flow on error in special handling
          }

          if (correlationIdentifier && !isDispatched) {
            // Generic Fasit error handling (your new case: systemName = "fasit")
            dispatch({
              type: 'notify',
              payload: {
                correlationIdentifier,
                severity: severity ?? 'error',
                status,
                detail,
                title,
                timestamp,
                helpLink,
                messageTitle,
              },
            });
            isDispatched = true;
          }
        }

        return Promise.reject(error);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
